import { ModalNavParams } from '@euler/app/Routes';
import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { useMediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { InspectionResultFormatter } from '@euler/app/flows/report/functions';
import { matchCustomIssueInfluenceFactor } from '@euler/app/flows/task/pages/inspection/functions/customIssueInfluence';
import { SiteInspectionCard } from '@euler/app/tv/components/inspection/types';
import { useDefectiveBadgeColorMap } from '@euler/app/tv/functions/useDefectiveBadgeColorMap';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import MaskedView from '@euler/components/MaskedView';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import {
  getDefectiveLevel,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  DefectiveLevel,
  DefectiveLevelColorMap,
  MediaObject,
} from '@euler/model';
import { InspectionReportItemType } from '@euler/model/report';
import { isNotNull } from '@euler/utils';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import { MotiView } from 'moti';
import { uniq } from 'ramda';
import { memo, useCallback, useMemo } from 'react';
import {
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Extrapolate,
  SharedValue,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';

const kMediaWidth = 294;
const kMediaHeight = 220;

type ItemModel = {
  name: string;
  label: string;
  type: InspectionReportItemType;
  defectiveClass?: string;
  defectiveLevel: DefectiveLevel;
  description?: string;
  valueRange?: string;
  maintenanceAdvice?: string;
  medias: MediaObject[];
};

export const InspectedSiteSlide = memo(
  ({
    width,
    height,
    scrollOffset,
    index,
    card,
    visible,
    style,
  }: {
    width: number;
    height: number;
    scrollOffset: SharedValue<number>;
    index: number;
    visible: boolean;
    card: SiteInspectionCard;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const animatedStyle = useAnimatedStyle(() => {
      const input = scrollOffset.value / width;
      const inputRange = [index - 1, index, index + 1];

      return {
        transform: [
          {
            scale: interpolate(
              input,
              inputRange,
              [0.8, 1, 0.8],
              Extrapolate.CLAMP,
            ),
          },
        ],
      };
    });

    return (
      <Animated.View
        key={index}
        css={`
          flex: 1;
          width: ${width}px;
          height: ${height}px;
          padding: ${rpx(40)}px;
          background-color: ${theme.task.siteDetail.bgColor};
          border-radius: ${rpx(40)}px;
        `}
        style={[style, animatedStyle]}
      >
        {visible ? <Content card={card} /> : null}
      </Animated.View>
    );
  },
);

const Content = memo(({ card }: { card: SiteInspectionCard }) => {
  const theme = usePresentationTheme();
  const { rpx } = useResponsiveContext();
  const colorMap = useDefectiveBadgeColorMap();
  const items = useMemo<ItemModel[]>(() => {
    return card.items.map<ItemModel>(item => {
      const name = InspectionResultFormatter.getInspectedItemDisplayName(item);
      const [label, valueRange] = InspectionResultFormatter.formatLabel(item);
      const defectiveLevel = getDefectiveLevel(
        item.abnormalLevel,
        item.severityLevel,
      );

      let description: string | undefined = undefined;
      if (item.type === InspectionReportItemType.CustomIssue) {
        const influenceFactor = matchCustomIssueInfluenceFactor(
          item.siteId == null
            ? item.siteInfluenceFactor
            : item.siteItemInfluenceFactor,
          item.defectiveClass,
        );
        description = [influenceFactor?.description, item.resultRemark]
          .filter(isNotNull)
          .join('，');
      } else {
        description =
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          item.abnormalDescription ||
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          item.resultRemark;
      }
      return {
        type: item.type,
        name,
        label: label ?? '',
        valueRange: valueRange ?? undefined,
        defectiveLevel,
        defectiveClass: item.defectiveClass,
        description: description?.trim() ?? '暂无相关故障影响说明',
        maintenanceAdvice: item.maintenanceAdvice,
        medias: item.medias.map<MediaObject>(x => ({
          id: x.id,
          type: /video/i.test(x.type) ? 'video' : 'image',
          url: x.url,
          coverUrl: x.coverUrl,
          description: x.remark,
          title: x.title,
          subTitle: x.subTitle,
        })),
      };
    });
  }, [card]);

  const medias = items.flatMap(item => item.medias);
  const defectiveLevel =
    items.length > 0 ? items[0].defectiveLevel : DefectiveLevel.Fine;

  const maintenanceAdvices = uniq(
    items
      .flatMap(x => x.maintenanceAdvice)
      .filter(x => x)
      .filter(isNotNull),
  );

  return (
    <MotiView
      from={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      css={`
        flex: 1;
      `}
    >
      <HStack
        justifyContent="flex-start"
        css={`
          margin-bottom: ${rpx(31)}px;
          flex-grow: 0;
          flex-shrink: 0;
          border-bottom-width: 1px;
          border-bottom-color: ${theme.task.siteDetail.separatorColor};
          padding-bottom: ${rpx(20)}px;
        `}
      >
        <View
          css={`
            width: ${rpx(8)}px;
            height: ${rpx(8)}px;
            border-radius: ${rpx(8) / 2}px;
            background-color: ${colorMap[defectiveLevel]};
            margin-right: ${rpx(10)}px;
          `}
        />
        <Typography.Label
          black
          size={rpx(40)}
          lineHeight={rpx(56)}
          color={theme.task.siteDetail.titleTextColor}
        >
          {`检测结果: ${card.name}`}
        </Typography.Label>
      </HStack>

      <MaskedView
        css={`
          flex: 1;
        `}
        maskElement={
          <LinearGradient
            colors={['black', 'black', 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            locations={[0, 0.8, 1]}
            css={`
              flex: 1;
            `}
          />
        }
      >
        <ScrollView
          showsVerticalScrollIndicator={true}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: rpx(120),
          }}
        >
          {/** medias and reference image */}
          <HStack
            justifyContent="flex-start"
            css={`
              margin-bottom: ${rpx(38)}px;
            `}
          >
            <MediaAlbumView medias={medias} />
            <SiteReferenceImageView
              imageUrl={
                card.type === 'inspected-site' ? card.site?.imgUrl : undefined
              }
            />
          </HStack>
          {/** item/custom issue inspection results */}
          {items.map((item, index) => (
            <ItemView key={index} item={item} colorMap={colorMap} />
          ))}
        </ScrollView>
      </MaskedView>

      <HStack
        justifyContent="flex-start"
        css={`
          flex-grow: 0;
          flex-shrink: 0;
          border-top-width: 1px;
          border-top-color: ${theme.task.siteDetail.separatorColor};
          padding-top: ${rpx(40)}px;
        `}
      >
        <Typography.Label
          bold
          size={rpx(32)}
          lineHeight={rpx(48)}
          color={theme.task.siteDetail.advice.labelTextColor}
        >
          推荐服务
        </Typography.Label>
        <HStack
          justifyContent="flex-start"
          css={`
            flex: 1;
            flex-wrap: wrap;
            margin-left: ${rpx(48)}px;
          `}
        >
          {maintenanceAdvices.map(advice => (
            <Center
              key={advice}
              css={`
                background-color: ${theme.task.siteDetail.advice.serviceItem
                  .bgColor};
                margin-left: ${rpx(36)}px;
                padding: ${rpx(13)}px ${rpx(24)}px;
                border-radius: ${rpx(12)}px;
              `}
            >
              <Typography.Label
                regular
                size={rpx(28)}
                lineHeight={rpx(42)}
                color={theme.task.siteDetail.advice.serviceItem.textColor}
              >
                {advice}
              </Typography.Label>
            </Center>
          ))}
        </HStack>
      </HStack>
    </MotiView>
  );
});

const ItemView = memo(
  ({
    item,
    colorMap,
    style,
  }: {
    item: ItemModel;
    colorMap: DefectiveLevelColorMap;
    style?: StyleProp<ViewStyle>;
  }) => {
    const color = colorMap[item.defectiveLevel];
    const {
      task: {
        siteDetail: { item: theme },
      },
    } = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          margin-bottom: ${rpx(24)}px;
        `}
        style={style}
      >
        <HStack justifyContent="flex-start">
          <View
            css={`
              width: ${rpx(20)}px;
              height: ${rpx(20)}px;
              border-radius: ${rpx(20) / 2}px;
              background-color: ${theme.labelTextColor};
              margin-right: ${rpx(10)}px;
            `}
          />
          <Typography.Label
            regular
            size={rpx(28)}
            lineHeight={rpx(42)}
            color={theme.labelTextColor}
          >
            {`${item.name}: `}
          </Typography.Label>
          <Typography.Label
            regular
            size={rpx(28)}
            lineHeight={rpx(42)}
            color={theme.valueTextColor}
          >
            {item.label}
          </Typography.Label>
          <Typography.Label
            regular
            size={rpx(28)}
            lineHeight={rpx(42)}
            color={theme.valueTipTextColor}
          >
            {item.valueRange ? ' [检测值]' : ''}
          </Typography.Label>
          <View
            css={`
              border-width: 1px;
              border-color: ${color};
              padding: 0px ${rpx(12)}px;
              border-radius: ${rpx(8)}px;
              margin-left: ${rpx(8)}px;
            `}
          >
            <Typography.Label
              light
              size={rpx(24)}
              lineHeight={rpx(36)}
              color={color}
            >
              {getDefectiveLevelText(item.defectiveLevel)}
            </Typography.Label>
          </View>
        </HStack>

        {item.valueRange != null && (
          <View
            css={`
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              padding: 4px 0;
            `}
          >
            <Typography.Label
              light
              size={rpx(24)}
              lineHeight={rpx(36)}
              color={theme.valueRangeLabelTextColor}
            >
              检测结果评估:{' '}
            </Typography.Label>
            <Typography.Label
              light
              size={rpx(24)}
              lineHeight={rpx(36)}
              color={theme.valueRangeTextColor}
            >
              {item.valueRange}
            </Typography.Label>
          </View>
        )}
        {item.description != null && (
          <Typography.Label
            light
            size={rpx(20)}
            lineHeight={rpx(36)}
            color={theme.descTextColor}
            css={`
              margin-top: ${rpx(10)}px;
            `}
          >
            {item.description}
          </Typography.Label>
        )}
      </View>
    );
  },
);

const MediaAlbumView = memo(
  ({
    medias,
    style,
  }: {
    medias: MediaObject[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const navigation = useNavigation<StackNavigationProp<ModalNavParams>>();

    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const [mediaWidth, mediaHeight] = [rpx(kMediaWidth), rpx(kMediaHeight)];

    const videos = medias.filter(x => x.type === 'video');
    const images = medias.filter(x => x.type === 'image');

    const onVideoPress = useCallback(
      (media: MediaObject) => {
        navigation.navigate('_mediaPreview', { type: 'video', url: media.url });
      },
      [navigation],
    );

    const {
      isImageViewerOpen,
      setIsImageViewerOpen,
      imageUrls,
      currentImageIndex,
      onMediaPress,
    } = useMediaCarousel(images);

    if (medias.length === 0) return null;

    return (
      <HStack style={style} justifyContent="flex-start">
        <Animated.View
          css={`
            width: ${mediaWidth}px;
            height: ${mediaHeight}px;
            border-radius: ${rpx(20)}px;
            overflow: hidden;
            margin-right: ${rpx(40)}px;
            flex-grow: 0;
          `}
        >
          <ImageViewer
            images={imageUrls}
            index={currentImageIndex}
            show={isImageViewerOpen}
            onDismiss={() => setIsImageViewerOpen(false)}
          />
          <Animated.View
            css={`
              width: ${mediaWidth * medias.length}px;
              height: ${mediaHeight}px;
              flex-direction: row;
              align-items: stretch;
            `}
          >
            {images.map(media => (
              <MediaCoverView
                key={media.id}
                media={media}
                TouchableComponent={TouchableOpacity}
                onPress={onMediaPress}
                activityIndicatorSize={rpx(40)}
                playIconSize={rpx(60)}
                noPlaceholder={
                  media.type === 'image' && media.url.includes('#')
                }
                containerStyle={{
                  borderRadius: rpx(20),
                  overflow: 'hidden',
                  width: mediaWidth,
                  height: mediaHeight,
                  flex: 1,
                }}
              />
            ))}
          </Animated.View>
          <Center
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: ${theme.task.siteDetail.media
                .counterOverlayBgColor};
              padding: ${rpx(10)}px 0;
            `}
          >
            <Typography.Label
              regular
              size={rpx(24)}
              lineHeight={rpx(36)}
              color={theme.task.siteDetail.media.counterOverlayTextColor}
            >
              {`${images.length}张照片`}
            </Typography.Label>
          </Center>
        </Animated.View>
        <View>
          {videos.map(media => (
            <MediaCoverView
              key={media.id}
              media={media}
              TouchableComponent={TouchableOpacity}
              onPress={onVideoPress}
              activityIndicatorSize={rpx(40)}
              playIconSize={rpx(60)}
              containerStyle={{
                borderRadius: rpx(20),
                overflow: 'hidden',
                width: mediaWidth,
                height: mediaHeight,
                marginRight: rpx(40),
              }}
            />
          ))}
        </View>
      </HStack>
    );
  },
);

const SiteReferenceImageView = memo(
  ({
    imageUrl,
    style,
  }: {
    imageUrl?: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const preview = usePreviewImage();
    const [mediaWidth, mediaHeight] = [rpx(kMediaWidth), rpx(kMediaHeight)];
    const onPress = useCallback(() => {
      if (imageUrl == null) return;
      preview({ imageUrls: [imageUrl], index: 0 });
    }, [imageUrl, preview]);

    return (
      <TouchableOpacity
        css={`
          width: ${mediaWidth}px;
          height: ${mediaHeight}px;
          border-radius: ${rpx(20)}px;
          overflow: hidden;
          justify-content: center;
          align-items: center;
        `}
        style={style}
        onPress={onPress}
      >
        {imageUrl == null ? (
          <Center
            css={`
              width: ${mediaWidth}px;
              height: ${mediaHeight}px;
              background-color: ${theme.task.siteDetail.media
                .placeholderBgColor};
            `}
          >
            <Typography.Label
              medium
              color={theme.task.siteDetail.media.placeholderIconColor}
              size={rpx(36)}
              css={`
                text-align: center;
              `}
            >
              暂无参考图片
            </Typography.Label>
          </Center>
        ) : (
          <Img
            uri={imageUrl}
            css={`
              width: ${mediaWidth}px;
              height: ${mediaHeight}px;
            `}
          />
        )}
        <Center
          css={`
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: ${imageUrl
              ? theme.task.siteDetail.media.counterOverlayBgColor
              : theme.task.siteDetail.media.titleBgColor};
            padding: ${rpx(10)}px 0;
          `}
        >
          <Typography.Label
            regular
            size={rpx(24)}
            lineHeight={rpx(36)}
            color={theme.task.siteDetail.media.titleTextColor}
          >
            正常状态参考照片
          </Typography.Label>
        </Center>
      </TouchableOpacity>
    );
  },
);
